import { useState, useEffect, useRef } from "react";
import useAudioDevices from "../../../hooks/useAudioDevices";
import { isMobileDevice } from "../../../utils/isMobile";

export default function useAudioSetup({ toast }) {
  const isMobile = isMobileDevice();
  const [devicesFetched, setDevicesFetched] = useState(false);
  const [showAudioSettings, setShowAudioSettings] = useState(false);
  const cachedOutputId = useRef(null); // 🧠 remember last chosen speaker

  const {
    inputDevices,
    outputDevices,
    selectedInputId,
    setSelectedInputId,
    selectedOutputId,
    setSelectedOutputId,
    fetchDevices,
  } = useAudioDevices();

  const toggleAudioSettings = () => setShowAudioSettings((v) => !v);

  // ✅ When user selects an output device
  const handleOutputChange = async (val) => {
    setSelectedOutputId(val);
    cachedOutputId.current = val;

    try {
      const audioEl = document.getElementById("assistant-audio");
      if (!audioEl) {
        console.log("[AudioSetup] No audio element yet — caching sinkId for later.");
        return;
      }

      if (audioEl.setSinkId) {
        await audioEl.setSinkId(val);
        console.log("[AudioSetup] Applied sinkId immediately:", val);
      } else {
        throw new Error("setSinkId not supported");
      }
    } catch (err) {
      console.warn("Failed to set audio output now:", err);
      // Don’t toast yet; this is expected before session
    }
  };

  // ✅ Automatically apply cached sinkId when audio element becomes active
  useEffect(() => {
    const interval = setInterval(async () => {
      const audioEl = document.getElementById("assistant-audio");
      if (!audioEl || !cachedOutputId.current) return;
      if (audioEl.srcObject && audioEl.setSinkId) {
        try {
          await audioEl.setSinkId(cachedOutputId.current);
          clearInterval(interval);
        } catch (err) {
          console.warn("[AudioSetup] Could not apply cached sinkId:", err);
        }
      }
    }, 500);

    return () => clearInterval(interval);
  }, []);

  // ✅ Get permission + enumerate devices
  useEffect(() => {
    const initDevices = async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        await fetchDevices();
        setDevicesFetched(true);
      } catch (err) {
        console.warn("Mic access denied:", err);
        toast?.({
          title: "Microphone blocked",
          description: "Please allow microphone access in your browser settings.",
          variant: "destructive",
        });
        setDevicesFetched(true);
      }
    };
    initDevices();
  }, [fetchDevices]);

  // ✅ Warn only if no input devices found (desktop)
  useEffect(() => {
    if (devicesFetched && !isMobile && inputDevices.length === 0) {
      toast?.({
        title: "Microphone not found",
        description: "Plug in or enable a microphone, then open settings to refresh.",
        variant: "warning",
      });
    }
  }, [devicesFetched, isMobile, inputDevices, toast]);

  return {
    isMobile,
    inputDevices,
    outputDevices,
    selectedInputId,
    setSelectedInputId,
    selectedOutputId,
    handleOutputChange,
    showAudioSettings,
    toggleAudioSettings,
    fetchDevices,
  };
}
